<script setup lang="ts">
/**
 * Conversation list of a chat: a search field with room for a „+“ button,
 * and the conversations as a listbox — avatar, name, time, preview, unread
 * counter (`NxChatListItem`).
 *
 * The caller owns the data AND its order (latest first, pinned on top, …):
 * the list shows `conversations` as given and only narrows them by the search
 * (`v-model:query`, matched against the name; `filter=false` leaves narrowing
 * to the caller, e.g. a server search). `v-model:active` is the open
 * conversation; choosing one also emits `select`.
 *
 * Keyboard: the list is one tab stop (`aria-activedescendant`). Arrows, Home
 * and End walk it, Enter or Space opens the highlighted conversation, ArrowUp
 * on the first one goes back to the search. In the search field ArrowDown
 * moves into the list, Enter opens the first match and Escape clears it.
 *
 * Fits `NxChatShell`'s `#sidebar` slot.
 */
import { computed, nextTick, ref, useId, useTemplateRef, watch } from 'vue';
import { AddIcon } from '@blpde/nx-icons';
import NxIconButton from '../IconButton/NxIconButton.vue';
import NxSearchInput from '../Inputs/NxSearchInput.vue';
import NxChatListItem from './NxChatListItem.vue';
import type { NxChatConversation } from './chatList';
import { foldText } from './text';

type ConversationId = NxChatConversation['id'];

const props = withDefaults(
  defineProps<{
    conversations: readonly NxChatConversation[];
    /** `false`: the search only updates `query`, the caller narrows the list. */
    filter?: boolean;
    /** Show the default „+“ button (emits `create`). The `#actions` slot replaces it. */
    creatable?: boolean;
    createLabel?: string;
    searchPlaceholder?: string;
    searchLabel?: string;
    listLabel?: string;
    emptyLabel?: string;
    unreadLabel?: (count: number) => string;
    /** Logical px of the avatars. */
    avatarSize?: number;
    /** Width of the list (CSS length). */
    width?: string;
    /** Heading above the search; the `#actions` slot then sits beside it. */
    title?: string;
    /** `plain`: on the surface, without edge line, round search and rounded rows. */
    surface?: 'sunken' | 'plain';
  }>(),
  {
    filter: true,
    creatable: false,
    createLabel: 'Neuer Chat',
    searchPlaceholder: 'Suche',
    searchLabel: 'Chats durchsuchen',
    listLabel: 'Chats',
    emptyLabel: 'Keine Chats gefunden',
    unreadLabel: (count: number) => `${count} ungelesen`,
    avatarSize: 30,
    width: 'var(--nxt-panel-width-md)',
    title: undefined,
    surface: 'sunken',
  }
);

const emit = defineEmits<{
  select: [conversation: NxChatConversation];
  create: [];
}>();

/** Id of the open conversation. */
const active = defineModel<ConversationId | null>('active', { default: null });
/** Text of the search field. */
const query = defineModel<string>('query', { default: '' });

defineSlots<{
  /** Next to the search field, instead of the default „+“ button. */
  actions?(): unknown;
  /** Between the search field and the list. */
  'head-extra'?(): unknown;
  /** Content of one row, instead of `NxChatListItem`. */
  item?(props: {
    conversation: NxChatConversation;
    active: boolean;
    highlighted: boolean;
  }): unknown;
  /** Shown when no conversation is left. */
  empty?(props: { query: string }): unknown;
}>();

const listId = useId();
const listbox = useTemplateRef<HTMLElement>('listbox');
const search = useTemplateRef<InstanceType<typeof NxSearchInput>>('search');

const visible = computed(() => {
  const q = foldText(query.value.trim());
  if (!props.filter || !q) return props.conversations;
  return props.conversations.filter((conversation) => foldText(conversation.name).includes(q));
});

/** Keyboard highlight inside the list; -1 = none. */
const highlight = ref(-1);
watch(visible, (list) => {
  if (highlight.value >= list.length) highlight.value = list.length - 1;
});
// A new search is a new list: the old highlight would point at another row.
watch(query, () => {
  highlight.value = -1;
});

function optionId(index: number): string {
  return `${listId}-option-${index}`;
}

function isActive(conversation: NxChatConversation): boolean {
  return active.value !== null && conversation.id === active.value;
}

function choose(conversation: NxChatConversation) {
  active.value = conversation.id;
  emit('select', conversation);
}

function moveHighlight(index: number) {
  highlight.value = index;
  nextTick(() => {
    // The options are the listbox's children, in order.
    const option = listbox.value?.children[index] as HTMLElement | undefined;
    // jsdom and some test hosts have no scrollIntoView.
    if (option && typeof option.scrollIntoView === 'function') {
      option.scrollIntoView({ block: 'nearest' });
    }
  });
}

function focusList(index: number) {
  if (!visible.value.length) return;
  moveHighlight(Math.min(Math.max(index, 0), visible.value.length - 1));
  listbox.value?.focus();
}

function onListFocus() {
  if (highlight.value >= 0 || !visible.value.length) return;
  const current = visible.value.findIndex(isActive);
  moveHighlight(current >= 0 ? current : 0);
}

function onListKeydown(event: KeyboardEvent) {
  const count = visible.value.length;
  if (!count) return;
  const index = highlight.value;
  switch (event.key) {
    case 'ArrowDown':
      moveHighlight(Math.min(count - 1, index + 1));
      break;
    case 'ArrowUp':
      if (index <= 0) {
        highlight.value = -1;
        search.value?.inputElement?.focus();
      } else {
        moveHighlight(index - 1);
      }
      break;
    case 'Home':
      moveHighlight(0);
      break;
    case 'End':
      moveHighlight(count - 1);
      break;
    case 'Enter':
    case ' ': {
      const conversation = visible.value[index];
      if (conversation) choose(conversation);
      break;
    }
    default:
      return;
  }
  event.preventDefault();
}

function onSearchKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    focusList(0);
  } else if (event.key === 'Enter') {
    const first = visible.value[0];
    if (!first) return;
    event.preventDefault();
    choose(first);
  } else if (event.key === 'Escape' && query.value) {
    event.preventDefault();
    event.stopPropagation();
    query.value = '';
  }
}
</script>

<template>
  <div
    class="nx-chat-channel-list"
    :class="{ 'is-plain': surface === 'plain', 'has-title': !!title }"
    :style="{ inlineSize: width }"
  >
    <div class="nx-chat-channel-list__head">
      <h2 v-if="title" class="nx-chat-channel-list__title">{{ title }}</h2>
      <div class="nx-chat-channel-list__search">
        <NxSearchInput
          ref="search"
          v-model="query"
          height="s"
          :placeholder="searchPlaceholder"
          :wrapper-style="{ width: '100%' }"
          :aria-label="searchLabel"
          :aria-controls="listId"
          @keydown="onSearchKeydown"
        />
      </div>
      <slot name="actions">
        <NxIconButton v-if="creatable" height="s" :title="createLabel" @click="emit('create')">
          <AddIcon class="nxt-icon-md" />
        </NxIconButton>
      </slot>
    </div>

    <div v-if="$slots['head-extra']" class="nx-chat-channel-list__extra">
      <slot name="head-extra" />
    </div>

    <ul
      :id="listId"
      ref="listbox"
      class="nx-chat-channel-list__list"
      role="listbox"
      :aria-label="listLabel"
      :tabindex="visible.length ? 0 : -1"
      :aria-activedescendant="highlight >= 0 ? optionId(highlight) : undefined"
      @focus="onListFocus"
      @keydown="onListKeydown"
    >
      <li
        v-for="(conversation, index) in visible"
        :id="optionId(index)"
        :key="conversation.id"
        class="nx-chat-channel-list__option"
        :class="{ 'is-active': isActive(conversation), 'is-highlighted': index === highlight }"
        role="option"
        :aria-selected="isActive(conversation)"
        @click="choose(conversation)"
      >
        <slot
          name="item"
          :conversation="conversation"
          :active="isActive(conversation)"
          :highlighted="index === highlight"
        >
          <NxChatListItem
            :name="conversation.name"
            :preview="conversation.preview"
            :preview-author="conversation.previewAuthor"
            :time="conversation.time"
            :unread="conversation.unread ?? 0"
            :avatar="conversation.avatar"
            :avatar-size="avatarSize"
            :active="isActive(conversation)"
            :unread-label="unreadLabel"
          />
        </slot>
      </li>
    </ul>

    <p v-if="!visible.length" class="nx-chat-channel-list__empty">
      <slot name="empty" :query="query">{{ emptyLabel }}</slot>
    </p>
  </div>
</template>

<style scoped>
.nx-chat-channel-list {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  max-inline-size: 100%;
  min-height: 0;
  background: var(--nxt-surface-sunken-bg);
  border-inline-end: var(--nxt-hairline) solid var(--nxt-border);
}

.nx-chat-channel-list__head {
  flex: none;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-sm) var(--nxt-space-sm) var(--nxt-space-xs);
}
.nx-chat-channel-list__search {
  flex: 1;
  min-width: 0;
}
.nx-chat-channel-list__title {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: var(--nxt-font-size-2xl);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: -0.025em;
  line-height: var(--nxt-line-height-tight);
  color: var(--nxt-heading-fg);
}
/* Title and actions in the first line, the search below them. */
.nx-chat-channel-list.has-title .nx-chat-channel-list__head {
  flex-wrap: wrap;
  padding-inline: var(--nxt-space-md);
}
.nx-chat-channel-list.has-title .nx-chat-channel-list__search {
  order: 3;
  flex-basis: 100%;
}
.nx-chat-channel-list__extra {
  flex: none;
}
.nx-chat-channel-list.is-plain {
  background: var(--nxt-surface-bg);
  border-inline-end: 0;
}
.nx-chat-channel-list.is-plain .nx-chat-channel-list__search :deep(input) {
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-sunken-bg);
  border-color: transparent;
}
.nx-chat-channel-list.is-plain .nx-chat-channel-list__list {
  padding-inline: var(--nxt-space-xs);
}
.nx-chat-channel-list.is-plain .nx-chat-channel-list__option {
  --chat-list-row-bg: var(--nxt-surface-bg);
  padding: 0.5em var(--nxt-space-xs);
  border-inline-start: 0;
  border-radius: var(--nxt-radius-lg);
}
.nx-chat-channel-list.is-plain .nx-chat-channel-list__option:hover,
.nx-chat-channel-list.is-plain .nx-chat-channel-list__option.is-highlighted {
  --chat-list-row-bg: var(--nxt-surface-sunken-bg);
  background: var(--nxt-surface-sunken-bg);
}
.nx-chat-channel-list.is-plain .nx-chat-channel-list__option.is-active {
  --chat-list-row-bg: var(--nxt-brand-subtle-bg);
  background: var(--nxt-brand-subtle-bg);
}

.nx-chat-channel-list__list {
  flex: 1 1 auto;
  min-height: 0;
  margin: 0;
  /* The global type reset sizes every bare <ul>/<p> at 16px medium. */
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-regular);
  padding: 0 0 var(--nxt-space-xs);
  list-style: none;
  overflow-y: auto;
  outline: none;
}

.nx-chat-channel-list__option {
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border-inline-start: calc(var(--nxt-hairline) * 3) solid transparent;
  cursor: pointer;
  transition: background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-chat-channel-list__option:hover,
.nx-chat-channel-list__option.is-highlighted {
  background: var(--nxt-surface-bg);
}
.nx-chat-channel-list__option.is-active {
  background: var(--nxt-surface-bg);
  border-inline-start-color: var(--nxt-brand-bg);
}
/* The list keeps DOM focus; the highlighted option carries the visible ring. */
.nx-chat-channel-list__list:focus-visible .nx-chat-channel-list__option.is-highlighted {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -2);
}

.nx-chat-channel-list__empty {
  margin: 0;
  padding: var(--nxt-space-sm);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
  text-align: center;
}

@media (prefers-reduced-motion: reduce) {
  .nx-chat-channel-list__option {
    transition: none;
  }
}
@media (forced-colors: active) {
  .nx-chat-channel-list__option.is-active {
    border-inline-start-color: Highlight;
  }
}
</style>
